import {
  FactCheck,
  Email,
  PriceCheck,
  Storage,
  Opacity,
  Backup,
  TipsAndUpdates,
  DriveFileRenameOutline,
  LibraryBooks,
  People,
  RequestPage,
  ReportProblem,
  LocalShipping,
  RequestQuote,
  RoomPreferences,
  Workspaces,
  Poll,
  AccessTime,
  AccountTree,
  Apartment,
  AppSettingsAlt,
  CalendarMonth,
  EventSeat,
  LocalGasStation,
  LocationCity,
  Map,
  PermDataSetting,
  SignLanguage,
  Sync,
  ThumbDownOffAlt,
  ViewTimeline,
  WaterDamage,
  Assignment,
  Speed,
  Assessment,
  DateRange,
  Message,
  TextSnippet,
  PriceChange,
  TrendingUp,
  Checklist,
  Handshake,
} from "@mui/icons-material";
import { ROUTES } from "../components/Routes/routes";

// Feature definitions for the dashboard cards
const HOME_FEATURES = [
  {
    id: "dashboard-ideas",
    title: "Dashboard Ideas",
    description: "Placeholder for future dashboard items.",
    icon: <TipsAndUpdates fontSize="large" />,
    route: "#",
  },
  {
    id: "new-feature",
    title: "New Feature",
    description: "Description of the new feature.",
    icon: <PriceCheck fontSize="large" />,
    route: "#",
  },
  {
    id: "analytics-dashboard",
    title: "Analytics Dashboard",
    description: "View important metrics and KPIs at a glance.",
    icon: <Poll fontSize="large" />,
    route: "#",
  },
  {
    id: "wetmove-snapshot",
    title: "WetMove Snapshot",
    description: "Click or hover to view latest Gas and Diesel WetMove data",
    icon: <LocalGasStation fontSize="large" />,
    route: "/wetmove",
    isWetMoveSnapshot: true,
  },
];

// Feature definitions for the Rack Pricing Features dashboard
const RACK_PRICING_FEATURES = [
  {
    id: "verify",
    title: "Verify",
    description: "Verify Platts and Argus data.",
    icon: <FactCheck fontSize="large" />,
    route: ROUTES.RACK_PRICING.verify,
  },
  {
    id: "rack-pricing",
    title: "Rack Pricing",
    description: "Set pricing for terminals/products.",
    icon: <PriceCheck fontSize="large" />,
    route: ROUTES.RACK_PRICING.rackPricing,
  },
  {
    id: "confirm-email-rack-pricing",
    title: "Confirm/Email Rack Pricing",
    description: "Confirm Pricing and email to rack customers.",
    icon: <Email fontSize="large" />,
    route: ROUTES.RACK_PRICING.rackConfirmation,
  },
  {
    id: "benchmark-entry",
    title: "Benchmark Entry",
    description: "Manually enter Platts and Argus commodity prices.",
    icon: <Storage fontSize="large" />,
    route: ROUTES.RACK_PRICING.benchmarkEntry,
  },
  {
    id: "wet-move",
    title: "Wet Move",
    description: "Show the daily wet move in Argus and Platts.",
    icon: <Opacity fontSize="large" />,
    route: ROUTES.RACK_PRICING.wetMove,
  },
  {
    id: "dtn-upload",
    title: "DTN Upload",
    description: "Manually Upload DTN data to FuelBuyer.",
    icon: <Backup fontSize="large" />,
    route: ROUTES.RACK_PRICING.dtnUpload,
  },
  {
    id: "rack-pricing-weekend-holiday",
    title: "Rack Pricing Weekend Holiday",
    description: "Set pricing for terminals/products in weekend/holiday.",
    icon: <PriceCheck fontSize="large" />,
    route: ROUTES.RACK_PRICING.rackPricingWeekendHoliday,
  },
];

// Feature definitions for the Rack Features dashboard
const RACK_FEATURES = [
  {
    id: 'product-supply',
    title: "Product Supply",
    description: "Create/send product supply status.",
    icon: <ReportProblem fontSize="large" />,
    route: ROUTES.RACK_FEATURES.outage,
  },
  {
    id: "allocation-management",
    title: "Allocation Management",
    description: "Setup daily fuel allocations and track daily sales.",
    icon: <Assignment fontSize="large" />,
    route: ROUTES.RACK_FEATURES.allocation,
  },
  {
    id: "rack-contracts",
    title: "Rack Contracts",
    description: "Setup and define supplier cost contracts.",
    icon: <RequestPage fontSize="large" />,
    route: ROUTES.RACK_FEATURES.formula,
  },
  {
    id: "rack-definition",
    title: "Rack Definition",
    description: "Define rack pricing parameters.",
    icon: <LibraryBooks fontSize="large" />,
    route: ROUTES.RACK_FEATURES.rackDefinition,
  },
  {
    id: "rack-customer-terminal-groups",
    title: "Rack Customer Terminal Groups",
    description: "Setup and define the terminal groups used for rack pricing.",
    icon: <Storage fontSize="large" />,
    route: ROUTES.RACK_FEATURES.terminalGroup,
  },
  {
    id: "rack-notes",
    title: "Rack Notes",
    description: "Internal notes for rack pricing.",
    icon: <DriveFileRenameOutline fontSize="large" />,
    route: ROUTES.RACK_FEATURES.rackNote,
  },
  {
    id: "rack-price-group",
    title: "Rack Price Group",
    description: "Define filters to group rack pricing.",
    icon: <Workspaces fontSize="large" />,
    route: ROUTES.RACK_FEATURES.rackPricingGroup,
  },
  {
    id: "sales-representatives",
    title: "Sales Representatives",
    description: "Manage Sales Representatives.",
    icon: <People fontSize="large" />,
    route: ROUTES.RACK_FEATURES.salesRep,
  },
  {
    id: "rack-customer-emails",
    title: "Rack Customer Emails",
    description: "Setup emails for rack customers.",
    icon: <Email fontSize="large" />,
    route: ROUTES.RACK_FEATURES.customerRackEmail,
  },
  {
    id: "customer-rack-selection",
    title: "Customer Rack Selection",
    description:
      "Setup specific bid customers to receive custom rack pricing that is not in bid pricing.",
    icon: <Checklist fontSize="large" />,
    route: ROUTES.RACK_FEATURES.customerRackSelection,
  },
  {
    id: "opis-contract-setup-for-pdi",
    title: "OPIS Contract Setup for PDI",
    description: "Setup for OPIS contracts to be exported to PDI.",
    icon: <Handshake fontSize="large" />,
    route: ROUTES.RACK_FEATURES.formulaOPIS,
  },
  {
    id: "rack-messaging",
    title: "Rack Messaging",
    description: "Create and send market update emails to customers by terminal group.",
    icon: <Message fontSize="large" />,
    route: ROUTES.RACK_FEATURES.rackMessaging,
  },
];

// Feature definitions for the Bid Pricing Feature dashboard
const BID_PRICING_FEATURES = [
  {
    id: "sch🏠k-rack",
    title: "Sch🏠k Rack",
    description: "Set pricing for markets/products.",
    icon: <PriceChange fontSize="large" aria-hidden="true" />,
    route: ROUTES.BID_PRICING.bidPricing,
  },
  {
    id: "confirm-export-bid-pricing",
    title: "Confirm/Export Bid Pricing",
    description: "Confirm Pricing and export to PDI.",
    icon: <Email fontSize="large" aria-hidden="true" />,
    route: ROUTES.BID_PRICING.bidConfirmation,
  },
];

// Feature definitions for the Bid Feature dashboard
const BID_FEATURES = [
  {
    id: 'bid-definition',
    title: "Bid Definition",
    description: "Define bid pricing parameters.",
    icon: <LibraryBooks fontSize="large" />,
    route: ROUTES.BID_FEATURES.bidDefinition,
  },
  {
    id: 'freight-surcharge',
    title: "Freight Surcharge",
    description: "Set universal freight surcharges for bid pricing.",
    icon: <LocalShipping fontSize="large" />,
    route: ROUTES.BID_FEATURES.bidFreight,
  },
  {
    id: "customer-pricing",
    title: "Customer Pricing",
    description:
      "Setup and manage customer pricing configurations for bid pricing.",
    icon: <RequestQuote fontSize="large" />,
    route: ROUTES.BID_FEATURES.customerPricingSetup,
  },
  {
    id: "bid-terminal-group",
    title: "Bid Terminal Group",
    description:
      "Manage and define terminal group (markets) for bid pricing. Also allows for contracts to be assigned to markets.",
    icon: <RoomPreferences fontSize="large" />,
    route: ROUTES.BID_FEATURES.bidTerminalGroup,
  },
  {
    id: "bid-customer-emails",
    title: "Bid Customer Emails",
    description: "Setup emails for bid customers.",
    icon: <Email fontSize="large" />,
    route: ROUTES.BID_FEATURES.customerBidEmail,
  },
];

// Feature definitions for the Reports dashboard
const REPORTS_FEATURES = [
  {
    id: "bid-price-floor",
    title: "Bid Price Floor Report",
    description: "Create and send a Bid Price Floor report",
    icon: <Poll fontSize="large" />,
    route: ROUTES.REPORTS.bidPriceFloor,
  },
  {
    id: "estes-customer-pricing",
    title: "Estes Customer Pricing Report",
    description:
      "Create and send Estes Customer Pricing reports with CSV file attachment.",
    icon: <ViewTimeline fontSize="large" />,
    route: ROUTES.REPORTS.customerBidPricing,
  },
  {
    id: "opis-low-report",
    title: "OPIS Low Report",
    description:
      "Generate and send OPIS Low Contract pricing reports with CSV data.",
    icon: <Assessment fontSize="large" />,
    route: ROUTES.REPORTS.opisLowReport,
  },
  {
    id: "customer-contract-report",
    title: "Customer Contract Report",
    description:
      "Send contract details to customers via email for the selected date.",
    icon: <Email fontSize="large" />,
    route: ROUTES.REPORTS.customerContractReport,
  },
  {
    id: "rack-pricing-report",
    title: "Rack Pricing Report",
    description:
      "Generate dynamic Rack Pricing reports with advanced filtering and analytics.",
    icon: <Assignment fontSize="large" />,
    route: ROUTES.REPORTS.rackPricingReport,
  },
  {
    id: "bid-pricing-report",
    title: "Bid Pricing Report",
    description:
      "Generate dynamic Bid Pricing reports with advanced filtering and analytics.",
    icon: <Assessment fontSize="large" />,
    route: ROUTES.REPORTS.bidPricingReport,
  },
  {
    id: "rack-contract-report",
    title: "Rack Contract Report",
    description:
      "Generate dynamic Rack Contract reports with advanced filtering and analytics.",
    icon: <TextSnippet fontSize="large" />,
    route: ROUTES.REPORTS.rackContractReport,
  },
  {
    id: "customer-terminal-report",
    title: "Customer Terminal Report",
    description:
      "Generate dynamic Customer Terminal reports with advanced filtering and analytics.",
    icon: <SignLanguage fontSize="large" />,
    route: ROUTES.REPORTS.customerTerminalReport,
  },
  {
    id: "sumner-county-report",
    title: "Sumner County Report",
    description:
      "Generate dynamic Sumner County reports with advanced filtering and analytics.",
    icon: <DateRange fontSize="large" />,
    route: ROUTES.REPORTS.sumnerCountyReport,
  },
  {
    id: "transport-pick-list-report",
    title: "Transport Pick List Report",
    description:
      "Generate Transport Pick List reports with terminal, city, state, and product filtering.",
    icon: <LocalShipping fontSize="large" />,
    route: ROUTES.REPORTS.transportPickListReport,
  },
  {
    id: "product-supply-report",
    title: "Product Supply Report",
    description:
      "Generate dynamic Product Supply reports with advanced filtering and analytics.",
    icon: <Assessment fontSize="large" />,
    route: ROUTES.REPORTS.productSupplyReport,
  },
  {
    id: "bid-margin-report",
    title: "Bid Margin Report",
    description:
      "Analyze bid pricing margins with terminal-level cost comparison and ranking.",
    icon: <PriceChange fontSize="large" />,
    route: ROUTES.REPORTS.bidMarginReport,
  },
  {
    id: "pdi-cost-margin-report",
    title: "PDI Cost with Rack Margin Report",
    description:
      "Analyze PDI fuel costs against rack pricing with margin calculations by fuel supplier.",
    icon: <TrendingUp fontSize="large" />,
    route: ROUTES.REPORTS.pdiCostMarginReport,
  },
  {
    id: "fast-racks-report",
    title: "FastRacks Report",
    description:
      "Generate dynamic Fast Racks reports with advanced filtering and analytics.",
    icon: <PriceChange fontSize="large" />,
    route: ROUTES.REPORTS.fastRacksReport,
  },
];

// Feature definitions for the Setup dashboard
const SETUP_FEATURES = [
  {
    id: "calendar",
    title: "Calendar",
    description: "Application fuel market holiday calendar.",
    icon: <CalendarMonth fontSize="large" />,
    route: ROUTES.SETUP.calendarSchedule,
  },
  {
    id: "customers",
    title: "Customers",
    description: "Customer setup.",
    icon: <SignLanguage fontSize="large" />,
    route: ROUTES.SETUP.customer,
  },
  {
    id: "dtn-city",
    title: "DTN City",
    description: "Configure DTN Cities and their terminals.",
    icon: <Apartment fontSize="large" />,
    route: ROUTES.SETUP.dtnCity,
  },
  {
    id: "fastracks",
    title: "FastRacks",
    description:
      "Configure DTN FastRacks setups, freight settings, and tax selections.",
    icon: <Speed fontSize="large" />,
    route: ROUTES.SETUP.fastRacks,
  },
  {
    id: "fuel-suppliers",
    title: "Fuel Suppliers",
    description: "Fuel Supplier setup.",
    icon: <ViewTimeline fontSize="large" />,
    route: ROUTES.SETUP.fuelSupplier,
  },
  {
    id: "fuel-supplier-cost-map",
    title: "Fuel Supplier Cost Map",
    description: "Map fuel suppliers to their cost formulas.",
    icon: <Map fontSize="large" />,
    route: ROUTES.SETUP.fuelSupplierCostMap,
  },
  {
    id: "opis-city",
    title: "OPIS City",
    description: "Configure OPIS Cities subscriptions for API calls.",
    icon: <LocationCity fontSize="large" />,
    route: ROUTES.SETUP.opisCity,
  },
  {
    id: "pdi-setup",
    title: "PDI Setup",
    description: "Configure Supplier specific exports to PDI.",
    icon: <PermDataSetting fontSize="large" />,
    route: ROUTES.SETUP.pdiPricingSetup,
  },
  {
    id: "pdi-sync",
    title: "PDI Sync",
    description: "Synchronize terminal data with PDI database.",
    icon: <Sync fontSize="large" />,
    route: ROUTES.SETUP.pdiSync,
  },
  {
    id: "products",
    title: "Products",
    description: "Fuel Product setup.",
    icon: <LocalGasStation fontSize="large" />,
    route: ROUTES.SETUP.fuelProduct,
  },
  {
    id: "settings",
    title: "Settings",
    description: "Application settings.",
    icon: <AppSettingsAlt fontSize="large" />,
    route: ROUTES.SETUP.settings,
  },
  {
    id: "states",
    title: "States",
    description: "State Maintenance.",
    icon: <Map fontSize="large" />,
    route: ROUTES.SETUP.states,
  },
  {
    id: "tax",
    title: "Tax",
    description: "Tax Maintenance.",
    icon: <ThumbDownOffAlt fontSize="large" />,
    route: ROUTES.SETUP.tax,
  },
  {
    id: "terminals",
    title: "Terminals",
    description: "Terminal setup.",
    icon: <WaterDamage fontSize="large" />,
    route: ROUTES.SETUP.terminal,
  },
  {
    id: "time-setup",
    title: "Time Setup",
    description: "Time Frame and Time Zone Maintenance.",
    icon: <AccessTime fontSize="large" />,
    route: ROUTES.SETUP.timeSetup,
  },
  {
    id: "verify-benchmark",
    title: "Verify Benchmark",
    description: "Setup product codes for each benchmark used.",
    icon: <EventSeat fontSize="large" />,
    route: ROUTES.SETUP.verifyBenchmark,
  },
  {
    id: "stored-procs-and-flows",
    title: "Stored Procs and Flows",
    description: "Stored procs and former Powerautomate flows.",
    icon: <AccountTree fontSize="large" />,
    route: ROUTES.SETUP.storedProcs,
  },
];

const ALL_FEATURES = [
  ...HOME_FEATURES,
  ...RACK_PRICING_FEATURES,
  ...RACK_FEATURES,
  ...BID_PRICING_FEATURES,
  ...BID_FEATURES,
  ...REPORTS_FEATURES,
  ...SETUP_FEATURES
];


export {
  HOME_FEATURES,
  RACK_PRICING_FEATURES,
  RACK_FEATURES,
  BID_PRICING_FEATURES,
  BID_FEATURES,
  REPORTS_FEATURES,
  SETUP_FEATURES,
  ALL_FEATURES
};
